import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiService } from '../../services/api';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useAppStore } from '../../store/appStore';
import { useAppContext } from '../../contexts/AppContext';
import { DocumentMessageTemplateField } from './DocumentMessageTemplateField';
import type { DocumentLanguage, DocumentType } from '../../utils/documentTypes';
import type { DocumentMessageTemplate } from '../../utils/documentTemplateDraft';

export interface DocumentSendSource { orderId?: string; quotationId?: string; vendorId?: string }

interface Props {
  type: DocumentType;
  source: DocumentSendSource;
  document: { url: string; fileName?: string; documentNumber?: string; orders?: number };
  recipient?: { name?: string; phone?: string };
  language?: DocumentLanguage;
  /** Called before leaving to create a message template (close the dialog). */
  onLeave?: () => void;
}

/** Mappings used on demand, remembered per business · document type · message template (this device). */
type Memory = { last?: string; byTemplate: Record<string, DocumentMessageTemplate['mappings']> };
const memoryKey = (businessId: string, type: string) => `documentSend:v1:${businessId}:${type}`;
async function readMemory(key: string): Promise<Memory> {
  try { const raw = await AsyncStorage.getItem(key); const parsed = raw ? JSON.parse(raw) : null; return parsed?.byTemplate ? parsed : { byTemplate: {} }; } catch { return { byTemplate: {} }; }
}

/**
 * Send the document just created, now, with a WhatsApp template the user picks: values mapped from
 * the document (prefilled from the last send with that template, else the document type's
 * automatic-message setup), the PDF attached when the template has a file header.
 */
export const DocumentSendSection: React.FC<Props> = ({ type, source, document, recipient, language, onLeave }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { navigateTo } = useAppContext();
  const tr = (key: string, values?: Record<string, any>) => t(`documents.send.${key}` as any, values);
  const businessId = useAppStore((s) => String(s.currentBusiness?._id || (s.currentBusiness as any)?.id || ''));
  const key = memoryKey(businessId, type);
  const [value, setValue] = useState<DocumentMessageTemplate | null>(null);
  const [ready, setReady] = useState(true);
  const [usable, setUsable] = useState<number | null>(null);
  const [phone, setPhone] = useState(recipient?.phone || '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const memory = useRef<Memory>({ byTemplate: {} });
  const fallback = useRef<DocumentMessageTemplate | null>(null);

  // Prefill: the last template used for this document type, else the automatic-message setup.
  useEffect(() => {
    let alive = true;
    (async () => {
      memory.current = await readMemory(key);
      try {
        const res = await apiService.getQuotationTemplates(type);
        const def = Array.isArray(res.data) ? res.data.find((x: any) => x.isDefault) : null;
        fallback.current = def?.messageTemplate?.templateId ? def.messageTemplate : null;
      } catch { fallback.current = null; }
      if (!alive) return;
      const last = memory.current.last;
      if (last) setValue({ templateId: last, mappings: memory.current.byTemplate[last] || fallbackMappings(last) });
      else if (fallback.current) setValue(fallback.current);
    })();
    return () => { alive = false; };
  }, [key]);

  const fallbackMappings = (templateId: string) => (fallback.current?.templateId === templateId ? fallback.current.mappings : []);

  // Picking another template brings back what was mapped for it before.
  const change = useCallback((next: DocumentMessageTemplate | null) => {
    setSent(null);
    setError(null);
    if (next && next.templateId !== value?.templateId && !next.mappings.length) {
      setValue({ templateId: next.templateId, mappings: memory.current.byTemplate[next.templateId] || fallbackMappings(next.templateId) });
    } else setValue(next);
  }, [value?.templateId]);

  const createTemplate = () => { onLeave?.(); navigateTo('template-management'); };

  const send = async () => {
    if (!value || !ready || !phone.trim()) return;
    setSending(true);
    setError(null);
    try {
      const res = await apiService.sendDocument({ ...source, type, document, phone: phone.trim(), language, messageTemplate: value });
      if (res.statusCode !== 1000 || !res.data) throw new Error((res as any).message || res.errors?.[0]?.message || tr('failed'));
      setSent(res.data.phone);
      memory.current = { last: value.templateId, byTemplate: { ...memory.current.byTemplate, [value.templateId]: value.mappings } };
      try { await AsyncStorage.setItem(key, JSON.stringify(memory.current)); } catch { /* best effort */ }
    } catch (e: any) {
      setError(e?.message || tr('failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <View style={styles.wrap}>
      {usable === 0 ? (
        <View style={[styles.notice, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="information-circle-outline" size={20} color={colors.primary as string} />
          <View style={styles.flex}>
            <Text style={[styles.noticeTitle, { textAlign: rtl.textStart }]}>{tr('noTemplatesTitle')}</Text>
            <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{tr('noTemplatesHint')}</Text>
            <TouchableOpacity style={[styles.create, { flexDirection: rtl.rowDirection }]} onPress={createTemplate} accessibilityRole="button">
              <Ionicons name="add-circle-outline" size={16} color={colors.white as string} />
              <Text style={styles.createText}>{tr('createTemplate')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}
      <View style={usable === 0 ? styles.hidden : undefined}>
        <DocumentMessageTemplateField
          mode="send"
          type={type}
          value={value}
          onChange={change}
          onReadyChange={setReady}
          onUsableCount={setUsable}
          onCreateNew={createTemplate}
          disabled={sending}
        />
      </View>
      {usable !== 0 && !!value && (
        <>
          <Text style={[styles.label, { textAlign: rtl.textStart }]}>{tr('to', { name: recipient?.name || '' })}</Text>
          <TextInput
            style={[styles.input, { textAlign: rtl.textStart }]}
            value={phone}
            onChangeText={(text) => { setPhone(text); setSent(null); }}
            keyboardType="phone-pad"
            placeholder="+966 5x xxx xxxx"
            placeholderTextColor={colors.text.disabled as string}
            editable={!sending}
          />
          {!!error && <Text style={[styles.error, { textAlign: rtl.textStart }]}>{error}</Text>}
          {sent ? (
            <View style={[styles.done, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="checkmark-circle" size={20} color={colors.success as string} />
              <Text style={[styles.doneText, { textAlign: rtl.textStart }]}>{tr('sent', { phone: sent })}</Text>
            </View>
          ) : (
            <TouchableOpacity style={[styles.send, { flexDirection: rtl.rowDirection }, (!ready || !phone.trim() || sending) && styles.disabled]} disabled={!ready || !phone.trim() || sending} onPress={send} accessibilityRole="button">
              {sending ? <ActivityIndicator color={colors.white as string} /> : <Ionicons name="logo-whatsapp" size={18} color={colors.white as string} />}
              <Text style={styles.sendText}>{tr('sendNow')}</Text>
            </TouchableOpacity>
          )}
          {!ready && <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{tr('fillValues')}</Text>}
        </>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  wrap: { gap: spacing.sm, marginTop: spacing.sm },
  flex: { flex: 1, gap: 4 },
  hidden: { display: 'none' },
  notice: { gap: spacing.sm, padding: spacing.md, borderRadius: 12, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  noticeTitle: { fontSize: typography.fontSize.sm, fontWeight: '700', color: colors.text.primary },
  hint: { fontSize: typography.fontSize.xs, lineHeight: 18, color: colors.text.secondary },
  create: { alignSelf: 'flex-start', alignItems: 'center', gap: 6, marginTop: spacing.xs, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, borderRadius: 10, backgroundColor: colors.primary },
  createText: { color: colors.white, fontWeight: '700', fontSize: typography.fontSize.sm },
  label: { fontSize: typography.fontSize.xs, fontWeight: '700', color: colors.text.secondary },
  input: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, fontSize: typography.fontSize.sm, color: colors.text.primary },
  error: { color: colors.error, fontSize: typography.fontSize.sm },
  send: { alignItems: 'center', justifyContent: 'center', gap: spacing.sm, paddingVertical: spacing.sm + 2, borderRadius: 10, backgroundColor: colors.success },
  sendText: { color: colors.white, fontWeight: '700', fontSize: typography.fontSize.sm },
  disabled: { opacity: 0.5 },
  done: { alignItems: 'center', gap: spacing.sm },
  doneText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary },
});
